import { useState, useEffect, useCallback } from "react";
import { MessageCircle, CheckCircle, XCircle, Clock, AlertTriangle } from "lucide-react";
import adminApi from "@/lib/adminApi";

export default function AdminWhatsApp() {
  const [status, setStatus] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statusRes, msgRes] = await Promise.all([
        adminApi.get("/api/admin/whatsapp/status"),
        adminApi.get("/api/admin/whatsapp/messages?limit=20"),
      ]);
      setStatus(statusRes.data);
      setMessages(msgRes.data.entries || []);
    } catch (err) { console.error("[AdminWhatsApp] fetch failed:", err); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="border border-white/[0.08] bg-[#060606] p-8 text-center text-xs text-white/30">Loading WhatsApp status...</div>;

  return (
    <div className="space-y-6" data-testid="admin-whatsapp">
      {/* Config Status */}
      <div className="border border-white/[0.08] bg-[#060606]">
        <div className="p-5 border-b border-white/[0.06] flex items-center gap-3">
          <MessageCircle size={16} className="text-[#CBA052]" />
          <h3 className="text-sm font-bold text-white">WhatsApp Integration</h3>
          <span className={`ml-auto text-xs font-semibold ${status?.configured ? "text-green-400" : "text-red-400"}`}>
            {status?.configured ? "Configured" : "Not configured"}
          </span>
        </div>
        <div className="p-5">
          <div className="space-y-2">
            <div className="flex justify-between py-2 border-b border-white/[0.04]">
              <span className="text-[11px] text-white/50 uppercase tracking-wider">Provider</span>
              <span className="text-xs text-white/70">{status?.provider || "Twilio"}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-white/[0.04]">
              <span className="text-[11px] text-white/50 uppercase tracking-wider">Account SID</span>
              <span className={`text-xs ${status?.account_sid_set ? "text-green-400" : "text-red-400"}`}>{status?.account_sid_set ? "Set" : "Missing"}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-white/[0.04]">
              <span className="text-[11px] text-white/50 uppercase tracking-wider">Auth Token</span>
              <span className={`text-xs ${status?.auth_token_set ? "text-green-400" : "text-red-400"}`}>{status?.auth_token_set ? "Set" : "Missing"}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-[11px] text-white/50 uppercase tracking-wider">From Number</span>
              <span className="text-xs text-white/70">{status?.from_number}</span>
            </div>
          </div>

          {!status?.configured && (
            <div className="mt-4 p-3 rounded bg-yellow-500/10 border border-yellow-500/20">
              <div className="flex items-center gap-2 text-xs text-yellow-400"><AlertTriangle size={12} /><span>WhatsApp not fully configured</span></div>
              <p className="text-[10px] text-yellow-400/60 mt-1">Set TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN, and TWILIO_WHATSAPP_FROM in backend .env</p>
            </div>
          )}
        </div>
      </div>

      {/* Message History */}
      <div className="border border-white/[0.08] bg-[#060606]">
        <div className="p-5 border-b border-white/[0.06] flex items-center gap-3">
          <Clock size={16} className="text-[#CBA052]" />
          <h3 className="text-sm font-bold text-white">Message History</h3>
          <span className="ml-auto text-[10px] text-white/30">{messages.length} messages</span>
        </div>
        <div className="p-5">
          {messages.length === 0 ? (
            <p className="text-xs text-white/30 text-center py-4">No WhatsApp messages recorded yet. Messages are logged when quote notifications are sent.</p>
          ) : (
            <div className="space-y-2">
              {messages.map((msg, i) => (
                <div key={`msg-${i}`} className="flex items-center gap-3 py-2 border-b border-white/[0.04] last:border-0">
                  {msg.status === "sent" ? <CheckCircle size={12} className="text-green-400 shrink-0" /> : <XCircle size={12} className="text-red-400 shrink-0" />}
                  <span className="text-[11px] text-white/60 flex-1 truncate">{msg.to || "Unknown"}: {msg.body?.slice(0, 80) || "(message)"}</span>
                  <span className="text-[10px] text-white/25">{msg.sent_at ? new Date(msg.sent_at).toLocaleString() : ""}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
